import {
  BackArrowIcon,
  Button,
  EyeIcon,
  TextInput,
  Typography,
} from "@/components/atoms";
import { OkDialog } from "@/components/molecules/dialogs/OkDialog";
import { BaseHeader } from "@/components/molecules/header/BaseHeader";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { router } from "expo-router";
import React, { useCallback, useState } from "react";
import { Platform, Pressable, StyleSheet, TouchableOpacity, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

export interface CurrentPasswordScreenProps {
  onPasswordVerified: (currentPassword: string) => void;
  onBackPress?: () => void;
}

export const CurrentPasswordScreen = React.memo<CurrentPasswordScreenProps>(
  function CurrentPasswordScreen({ onPasswordVerified, onBackPress }) {
    useCatalogDynatraceView(
      DT_SCREEN.REGISTRATION,
      "current_password_screen_viewed",
      true,
    );
    const { t } = useTranslation();
    const colors = useUIColors();
    const { isRTL } = useI18nContext();
    const insets = useSafeAreaInsets();

    const [currentPassword, setCurrentPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [errorDialogVisible, setErrorDialogVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const handleBackPress = useCallback(() => {
      if (onBackPress) {
        onBackPress();
      } else {
        router.back();
      }
    }, [onBackPress]);

    const handleContinue = useCallback(() => {
      if (!currentPassword.trim()) {
        setErrorMessage(t("auth.passwordRequired"));
        setErrorDialogVisible(true);
        return;
      }

      onPasswordVerified(currentPassword);
    }, [currentPassword, onPasswordVerified, t]);

    return (
      <View
        style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}
      >
        <BaseHeader
          leading={
            <TouchableOpacity
              onPress={handleBackPress}
              style={styles.backButtonContainer}
            >
              <BackArrowIcon size={24} color={colors.fontPrimary} />
            </TouchableOpacity>
          }
          middle={<View style={styles.placeholder} />}
          trailing={<View style={styles.placeholder} />}
        />

        <View
          style={[
            styles.content,
            { paddingTop: 16, paddingBottom: insets.bottom + 24 },
          ]}
        >
          <View style={styles.contentWrapper}>
            {/* Page Header */}
            <View style={styles.pageHeader}>
              <Typography
                variant="title1"
                color="fontPrimary"
                style={[styles.title,  Platform.OS === "web" && { textAlign: isRTL ? "right" : "left" }]} 
              >
                {t("auth.enterCurrentPasswordTitle")}
              </Typography>
              <Typography
                variant="body"
                color="fontPrimary"
                style={[
                  styles.subtitle,
                  Platform.OS === "web" && { textAlign: isRTL ? "right" : "left" },
                ]}
              >
                {t("auth.pleaseEnterCurrentPassword")}
              </Typography>
            </View>

            {/* Current Password Input */}
            <View style={styles.inputSection}>
              <View style={styles.inputWrapper}>
                <TextInput
                  label={t("auth.enterCurrentPassword")}
                  value={currentPassword}
                  onChangeText={setCurrentPassword}
                  secureTextEntry={!showPassword}
                  placeholder={t("auth.enterCurrentPassword")}
                  rightIcon={
                    <Pressable onPress={() => setShowPassword(!showPassword)}>
                      <EyeIcon
                        size={19}
                        color={colors.fontPrimary}
                        isVisible={showPassword}
                      />
                    </Pressable>
                  }
                />
              </View>
            </View>

            {/* Continue Button (directly below input) */}
            <View style={styles.buttonContainer}>
              <Button
                title={t("common.continue")}
                variant="primary"
                fullWidth={true}
                onPress={handleContinue}
                disabled={!currentPassword.trim()}
              />
            </View>
          </View>
        </View>

        {/* Error Dialog */}
        <OkDialog
          visible={errorDialogVisible}
          message={errorMessage}
          onOk={() => setErrorDialogVisible(false)}
        />
      </View>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    alignItems: "center",
  },
  contentWrapper: {
    width: "100%",
    maxWidth: 600,
  },
  pageHeader: {
    gap: 16,
    marginTop: 32,
    marginBottom: 32,
  },
  title: {
    fontSize: 28,
    fontWeight: "500",
  },
  subtitle: {
    // textAlign is now set dynamically based on isRTL
  },
  inputSection: {
    gap: 8,
    width: "100%", // Ensure section takes full width for RTL alignment
  },
  inputWrapper: {
    gap: 8,
    width: "100%", // Ensure wrapper takes full width for RTL alignment
  },
  buttonContainer: {
    marginTop: 24,
    paddingBottom: 12,
  },
  backButtonContainer: {
    height: 40,
    width: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  placeholder: {
    height: 40,
    width: 40,
  },
});

export default CurrentPasswordScreen;
